import React, { useEffect, useState } from "react";
import { useNotification } from "../../hooks/useNotification";
import Notification from "../ui/Notification";
import { useGetPatronageDetailsQuery, useUpdatePatronageMutation } from "../../api";

interface PatronageEditModalProps {
    isOpen: boolean;
    userId: number;
    onClose: () => void;
    onUpdated: () => void;
}

interface PatronageFormData {
    gold_immunity: string;
    gold_misses: string;
    gold_lost: boolean;
    silver_patron_amount_1: string;
    silver_patron_time_1: string;
    gold_patron_amount_1: string;
    gold_patron_time_1: string;
    silver_patron_enabled: boolean;
    gold_patron_enabled: boolean;
    silver_patron_coefficient: string;
    gold_patron_coefficient: string;
}

const PatronageEditModal: React.FC<PatronageEditModalProps> = ({
    isOpen,
    userId,
    onClose,
    onUpdated,
}) => {
    const { notification, hideNotification, showError, showSuccess } =
        useNotification();

    const { data, isLoading, refetch } = useGetPatronageDetailsQuery(userId, {
        skip: !isOpen,
        refetchOnMountOrArgChange: true,
    });
    const [updatePatronage, { isLoading: isUpdating }] =
        useUpdatePatronageMutation();

    const formatDateTimeLocal = (value?: string | null): string => {
        if (!value) return "";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        return date.toISOString().slice(0, 16);
    };

    const normalizeDateTime = (value: string): string | null => {
        if (!value) return null;
        if (value.length === 16 && value.includes("T")) {
            return `${value}:00`;
        }
        return value;
    };

    const formatDateTimeDisplay = (value?: string | null): string => {
        if (!value) return "-";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }
        return date.toLocaleString("ru-RU");
    };

    const [formData, setFormData] = useState<PatronageFormData>({
        gold_immunity: "0",
        gold_misses: "0",
        gold_lost: false,
        silver_patron_amount_1: "0",
        silver_patron_time_1: "",
        gold_patron_amount_1: "0",
        gold_patron_time_1: "",
        silver_patron_enabled: false,
        gold_patron_enabled: false,
        silver_patron_coefficient: "1",
        gold_patron_coefficient: "1",
    });

    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        if (!data?.data?.patronage) return;

        const p = data.data.patronage;
        setFormData({
            gold_immunity: String(p.gold_immunity ?? 0),
            gold_misses: String(p.gold_misses ?? 0),
            gold_lost: !!p.gold_lost,
            silver_patron_amount_1: String(p.silver_patron_amount_1 ?? 0),
            silver_patron_time_1: formatDateTimeLocal(p.silver_patron_time_1),
            gold_patron_amount_1: String(p.gold_patron_amount_1 ?? 0),
            gold_patron_time_1: formatDateTimeLocal(p.gold_patron_time_1),
            silver_patron_enabled: !!p.silver_patron_enabled,
            gold_patron_enabled: !!p.gold_patron_enabled,
            silver_patron_coefficient: String(
                p.silver_patron_coefficient ?? 1
            ),
            gold_patron_coefficient: String(
                p.gold_patron_coefficient ?? 1
            ),
        });
    }, [data, isOpen]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            await updatePatronage({
                userId,
                data: {
                    gold_immunity: parseInt(formData.gold_immunity, 10) || 0,
                    gold_misses: parseInt(formData.gold_misses, 10) || 0,
                    gold_lost: !!formData.gold_lost,
                    silver_patron_amount_1:
                        parseFloat(formData.silver_patron_amount_1) || 0,
                    silver_patron_time_1: normalizeDateTime(
                        formData.silver_patron_time_1
                    ),
                    gold_patron_amount_1:
                        parseFloat(formData.gold_patron_amount_1) || 0,
                    gold_patron_time_1: normalizeDateTime(
                        formData.gold_patron_time_1
                    ),
                    silver_patron_enabled: !!formData.silver_patron_enabled,
                    gold_patron_enabled: !!formData.gold_patron_enabled,
                    silver_patron_coefficient:
                        parseInt(formData.silver_patron_coefficient, 10) || 1,
                    gold_patron_coefficient:
                        parseInt(formData.gold_patron_coefficient, 10) || 1,
                },
            }).unwrap();

            showSuccess("Меценатство обновлено");
            // RTK Query автоматически обновит данные через invalidatesTags
            // Но для модалки явно перезапрашиваем, чтобы показать актуальные данные
            await refetch();
            onUpdated();
            onClose();
        } catch (error: any) {
            showError(
                error?.data?.message ||
                    error?.message ||
                    "Ошибка при обновлении меценатства"
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    const userLabel = data?.data?.user
        ? `${data.data.user.name || ""} ${data.data.user.surname || ""}`.trim()
        : "";
    const patronageInfo = data?.data?.patronage;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
            <Notification
                message={notification.message}
                type={notification.type}
                isVisible={notification.isVisible}
                onClose={hideNotification}
            />

            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-3xl max-h-[85vh] overflow-y-auto mx-4 my-8">
                <div className="flex justify-between items-center mb-4">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Меценатство {userLabel ? `— ${userLabel}` : ""}
                        </h2>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            Пользователь ID: {userId} {data?.data?.user?.secret_cookie ? `(Secret Cookie: ${data.data.user.secret_cookie})` : ""}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="flex flex-col gap-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="p-3 rounded border border-gray-200 dark:border-gray-700">
                                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                                        <input
                                            type="checkbox"
                                            name="silver_patron_enabled"
                                            checked={formData.silver_patron_enabled}
                                            onChange={handleInputChange}
                                            className="h-4 w-4"
                                        />
                                        Серебро: включено
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <label className="text-xs text-gray-700 dark:text-gray-300">
                                            Серебро: коэффициент
                                        </label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={10}
                                            step={1}
                                            name="silver_patron_coefficient"
                                            value={formData.silver_patron_coefficient}
                                            onChange={handleInputChange}
                                            className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>
                                </div>
                                <div className="p-3 rounded border border-gray-200 dark:border-gray-700">
                                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-2">
                                        <input
                                            type="checkbox"
                                            name="gold_patron_enabled"
                                            checked={formData.gold_patron_enabled}
                                            onChange={handleInputChange}
                                            className="h-4 w-4"
                                        />
                                        Золото: включено
                                    </label>
                                    <div className="flex items-center gap-2">
                                        <label className="text-xs text-gray-700 dark:text-gray-300">
                                            Золото: коэффициент
                                        </label>
                                        <input
                                            type="number"
                                            min={1}
                                            max={10}
                                            step={1}
                                            name="gold_patron_coefficient"
                                            value={formData.gold_patron_coefficient}
                                            onChange={handleInputChange}
                                            className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
                            <div className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                                Этап 1 (только просмотр)
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="p-3 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Серебро
                                    </div>
                                    <div className="text-sm text-gray-900 dark:text-white">
                                        Сумма: {patronageInfo?.silver_patron_amount ?? 0}
                                    </div>
                                    <div className="text-sm text-gray-900 dark:text-white">
                                        Время оплаты: {formatDateTimeDisplay(patronageInfo?.silver_patron_time)}
                                    </div>
                                </div>
                                <div className="p-3 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Золото
                                    </div>
                                    <div className="text-sm text-gray-900 dark:text-white">
                                        Сумма: {patronageInfo?.gold_patron_amount ?? 0}
                                    </div>
                                    <div className="text-sm text-gray-900 dark:text-white">
                                        Время оплаты: {formatDateTimeDisplay(patronageInfo?.gold_patron_time)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div className="grid grid-cols-1 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Серебро — этап 2
                                        </label>
                                        <input
                                            type="number"
                                            min={0}
                                            step={0.01}
                                            name="silver_patron_amount_1"
                                            value={formData.silver_patron_amount_1}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Время оплаты
                                        </label>
                                        <input
                                            type="datetime-local"
                                            name="silver_patron_time_1"
                                            value={formData.silver_patron_time_1}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div className="grid grid-cols-1 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Золото — этап 2
                                        </label>
                                        <input
                                            type="number"
                                            min={0}
                                            step={0.01}
                                            name="gold_patron_amount_1"
                                            value={formData.gold_patron_amount_1}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Время оплаты
                                        </label>
                                        <input
                                            type="datetime-local"
                                            name="gold_patron_time_1"
                                            value={formData.gold_patron_time_1}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Иммунитет
                                    </label>
                                    <input
                                        type="number"
                                        min={0}
                                        step={1}
                                        name="gold_immunity"
                                        value={formData.gold_immunity}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Пропуски
                                    </label>
                                    <input
                                        type="number"
                                        min={0}
                                        step={1}
                                        name="gold_misses"
                                        value={formData.gold_misses}
                                        onChange={handleInputChange}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div className="flex items-center">
                                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mt-5">
                                        <input
                                            type="checkbox"
                                            name="gold_lost"
                                            checked={formData.gold_lost}
                                            onChange={handleInputChange}
                                            className="h-4 w-4"
                                        />
                                        Потеряно
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={onClose}
                                disabled={isSaving}
                                className="px-4 py-2 text-gray-600 dark:text-gray-400 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:bg-gray-900 disabled:opacity-50"
                            >
                                Отмена
                            </button>
                            <button
                                type="submit"
                                disabled={isSaving || isUpdating}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isSaving || isUpdating ? (
                                    <div className="flex items-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Сохранение...
                                    </div>
                                ) : (
                                    "Сохранить"
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default PatronageEditModal;

